import axios from 'axios';

/**
 * Liest den Dateinamen aus einem Content-Disposition-Header.
 * Unterstützt sowohl `filename="…"` als auch RFC-5987 `filename*=UTF-8''…`.
 */
function dateinameAusHeader(cd?: string): string | null {
  if (!cd) return null;
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(cd);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''));
    } catch {
      /* fällt auf den einfachen Parameter zurück */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(cd);
  return plain?.[1]?.trim() ?? null;
}

/**
 * Holt eine geschützte Datei als Blob und gibt eine Object-URL + Dateinamen zurück.
 *
 * WICHTIG: Datei-Endpunkte hängen an `require_auth`. Ein direkter Browser-Aufruf
 * (`window.open` / `a.href` auf die API-URL) schickt KEINEN Authorization-Header
 * und führt zu 401 "Nicht eingeloggt". Daher wird die Datei über axios geholt
 * (globaler Interceptor setzt den Bearer-Token und behandelt 401).
 *
 * Die Object-URL eignet sich für Inline-Vorschau (iframe) UND Download. Der
 * Aufrufer ist für `URL.revokeObjectURL(objectUrl)` verantwortlich.
 *
 * @param url           Vollständige URL inkl. API_URL-Prefix.
 * @param fallbackName  Dateiname, falls der Server keinen Content-Disposition-Header sendet.
 */
export async function fetchDateiBlobUrl(
  url: string,
  fallbackName: string,
): Promise<{ objectUrl: string; filename: string }> {
  const resp = await axios.get(url, { responseType: 'blob' });
  const filename = dateinameAusHeader(resp.headers['content-disposition']) ?? fallbackName;
  return { objectUrl: URL.createObjectURL(resp.data as Blob), filename };
}

/** Stößt den Download einer bereits geladenen Object-URL an (ohne Revoke). */
export function triggerBlobDownload(objectUrl: string, filename: string): void {
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Lädt eine geschützte Datei herunter (Fetch + Download + Aufräumen).
 * Jede Dateiabfrage im Frontend MUSS diesen Helper (oder fetchDateiBlobUrl) nutzen.
 */
export async function downloadDatei(url: string, fallbackName: string): Promise<void> {
  const { objectUrl, filename } = await fetchDateiBlobUrl(url, fallbackName);
  triggerBlobDownload(objectUrl, filename);
  URL.revokeObjectURL(objectUrl);
}

/**
 * Wie {@link downloadDatei}, wirft aber nicht: liefert `null` bei Erfolg und
 * sonst eine fertige Meldung für die Fehlerzeile des Aufrufers. Gedacht für
 * Listen und Karten, die für einen Datei-Download keinen eigenen
 * Fehlerkanal aufmachen wollen.
 */
export async function downloadDateiOderFehler(
  url: string,
  fallbackName: string,
  bezeichnung = 'Datei',
): Promise<string | null> {
  try {
    await downloadDatei(url, fallbackName);
    return null;
  } catch {
    return `${bezeichnung} konnte nicht geladen werden.`;
  }
}
